import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  createBartenderConnectOnboarding,
  refreshBartenderConnectStatus,
  getBartenderConnectStatus,
} from "@/lib/connect.functions";
import { Button } from "@/components/ui/button";

type PayoutsSetupSearch = {
  from?: "stripe";
  redirect?: string;
};

export const Route = createFileRoute("/_authenticated/bartender/payouts-setup")({
  head: () => ({ meta: [{ title: "Set up payouts — The Hospitality Reserve" }] }),
  validateSearch: (search: Record<string, unknown>): PayoutsSetupSearch => ({
    from: search.from === "stripe" ? "stripe" : undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: PayoutsSetup,
});

export function PayoutsSetup() {
  const navigate = useNavigate();
  const { from, redirect } = Route.useSearch();
  const start = useServerFn(createBartenderConnectOnboarding);
  const refresh = useServerFn(refreshBartenderConnectStatus);
  const status = useServerFn(getBartenderConnectStatus);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const sync = async () => {
    setLoading(true);
    try {
      const s = await status();
      if (s.has_account) {
        const r = await refresh({ data: { environment: getStripeEnvironment() } });
        if (!("error" in r)) {
          setEnabled(r.enabled);
          setHasAccount(true);
        } else {
          setHasAccount(true);
          setEnabled(s.enabled);
        }
      } else {
        setHasAccount(false);
        setEnabled(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStripeReturn = async () => {
    setLoading(true);
    try {
      const s = await status();
      if (s.has_account) {
        const r = await refresh({ data: { environment: getStripeEnvironment() } });
        if (!("error" in r) && r.enabled) {
          setEnabled(true);
          setHasAccount(true);
          toast.success("Bank account connected. Redirecting to your dashboard…", {
            duration: 2000,
          });
          setTimeout(() => {
            navigate({ to: redirect || "/bartender", replace: true });
          }, 1500);
          return;
        }
      }
      await sync();
      toast.error("Bank connection could not be completed. Please try again.", {
        duration: 3000,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not verify payout status.", {
        duration: 3000,
      });
      await sync();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (from === "stripe") {
      handleStripeReturn();
    } else {
      sync();
    }
    /* eslint-disable-next-line */
  }, []);

  const openOnboardingUrl = (url: string) => {
    // Escape the Lovable preview iframe (or any iframe) by navigating the top-level window.
    try {
      if (window.top) {
        window.top.location.href = url;
        return;
      }
    } catch {
      // Cross-origin iframe: fall back to a new tab.
    }
    window.open(url, "_blank");
  };

  const beginOnboarding = async () => {
    setWorking(true);
    try {
      const origin = window.location.origin;
      const returnUrl = new URL(`${origin}/bartender/payouts-setup`);
      returnUrl.searchParams.set("from", "stripe");
      if (redirect) returnUrl.searchParams.set("redirect", redirect);
      const res = await start({
        data: {
          environment: getStripeEnvironment(),
          return_url: returnUrl.toString(),
          refresh_url: `${origin}/bartender/payouts-setup?refresh=1`,
        },
      });
      if ("error" in res) throw new Error(res.error);
      openOnboardingUrl(res.url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open Stripe onboarding.", {
        duration: 3000,
      });
      setWorking(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between border-b border-border">
        <div>
          <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
          <p className="font-display text-lg leading-tight">Hospitality</p>
        </div>
        <button onClick={signOut} className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent">
          Sign out
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <p className="text-center tracking-wide-luxury text-accent text-xs">One last step</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl text-center leading-tight">
            {enabled ? "Payout details active" : "Link your payout details"}
          </h1>
          <p className="mt-4 text-center text-sm text-muted-foreground leading-relaxed">
            {enabled
              ? "Your Stripe Connect account is linked and ready to receive payouts. You can re-run onboarding at any time to update your bank or identity details."
              : "Before browsing shifts, please connect your bank account through Stripe. This is how you'll receive payment securely after every engagement — no waiting, no chasing."}
          </p>

          <div className="mt-8 bg-white text-foreground p-6">
            {enabled ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <p className="text-sm font-medium">Stripe account connected</p>
                </div>
                <Button
                  onClick={beginOnboarding}
                  disabled={working || loading}
                  variant="outline"
                  className="w-full rounded-none border-foreground text-foreground hover:bg-foreground/5 tracking-wide-luxury py-6"
                >
                  {working ? "Opening Stripe…" : "Re-run onboarding / Update details"}
                </Button>
                <Link
                  to="/bartender"
                  className="block w-full text-center text-xs tracking-wide-luxury text-foreground/60 hover:text-accent transition-colors py-2"
                >
                  Back to dashboard
                </Link>
              </div>
            ) : (
              <>
                <ul className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="text-accent">·</span>
                    <span>Identity & bank details handled by Stripe — we never see them.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent">·</span>
                    <span>Takes about 2 minutes. You'll come straight back here.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent">·</span>
                    <span>Required once. Future payouts arrive automatically.</span>
                  </li>
                </ul>

                <Button
                  onClick={beginOnboarding}
                  disabled={working || loading}
                  className="mt-6 w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
                >
                  {working
                    ? "Opening Stripe…"
                    : hasAccount && !enabled
                      ? "Continue payout setup"
                      : "Connect with Stripe"}
                </Button>

                {hasAccount && !enabled && (
                  <button
                    onClick={sync}
                    disabled={loading}
                    className="mt-3 w-full text-xs tracking-wide-luxury text-foreground/60 hover:text-accent transition-colors py-2"
                  >
                    {loading ? "Checking…" : "I've completed setup — refresh"}
                  </button>
                )}

                <Link
                  to="/bartender"
                  className="mt-3 block w-full text-center text-xs tracking-wide-luxury text-foreground/40 hover:text-accent transition-colors py-2"
                >
                  Back to dashboard
                </Link>
              </>
            )}
          </div>

          <div className="mt-6 border border-border bg-muted/40 p-4">
            <p className="tracking-wide-luxury text-accent text-[10px]">Security Notice</p>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              All financial and banking details are processed exclusively and securely via Stripe Connect.
              The Hospitality Reserve never sees, accesses, or stores your bank accounts or sensitive
              financial records.
            </p>
          </div>

          <p className="mt-6 text-center text-[11px] tracking-wide-luxury text-muted-foreground/70">
            Powered by Stripe Connect
          </p>
        </div>
      </main>
    </div>
  );
}
